import React from 'react';
import { Link } from 'react-router-dom';
import { Project } from '../data/projects';
import {
  IconRocket, IconAI, IconPalette, IconZap, IconDollar, IconGear, IconSearch, IconGlobe,
  IconAudio, IconClipboard, IconWine, IconHeadphones, IconTerminal, IconTrophy
} from './Icons';
import './ProjectCard.css';

interface ProjectCardProps {
  project: Project;
}

const ProjectCard: React.FC<ProjectCardProps> = ({ project }) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return '#10b981';
      case 'beta':
        return '#f59e0b';
      case 'deprecated':
        return '#ef4444';
      default:
        return '#6b7280';
    }
  };

  const getIcon = (iconKey: string) => {
    switch (iconKey) {
      case 'rocket': return <IconRocket />;
      case 'bot': return <IconAI />;
      case 'palette': return <IconPalette />;
      case 'zap': return <IconZap />;
      case 'dollar': return <IconDollar />;
      case 'settings': return <IconGear />;
      case 'search': return <IconSearch />;
      case 'image': return <IconPalette />;
      case 'world': return <IconGlobe />;
      case 'audio': return <IconAudio />;
      case 'wine': return <IconWine />;
      case 'clipboard': return <IconClipboard />;
      case 'mic': return <IconAudio />;
      case 'headphones': return <IconHeadphones />;
      case 'terminal': return <IconTerminal />;
      case 'trophy': return <IconTrophy />;
      default: return <IconRocket />;
    }
  };

  return (
    <Link to={`/projects/${project.slug}`} className="project-card-link">
      <div className={`project-card-compact glass ${project.featured ? 'featured' : ''}`}>
        {project.featured && (
          <div className="featured-badge">Featured</div>
        )}

        {project.cardImage ? (
          <div className="project-card-image">
            <img src={project.cardImage} alt={project.name} loading="lazy" />
          </div>
        ) : (
          <div className="project-card-icon-fallback icon-glow">
            {getIcon(project.icon)}
          </div>
        )}

        <div className="project-card-header">
          {project.status !== 'archived' && (
            <div className="project-card-status" style={{ backgroundColor: getStatusColor(project.status) }}>
              {project.status}
            </div>
          )}
        </div>

        <h3 className="project-card-title">{project.name}</h3>
        <p className="project-card-tagline">{project.tagline}</p>
        <p className="project-card-description">{project.shortDescription}</p>

        <div className="project-card-tech">
          {project.techStack.slice(0, 4).map((tech, index) => (
            <span key={index} className="tech-badge-small">{tech}</span>
          ))}
          {project.techStack.length > 4 && (
            <span className="tech-badge-small more">+{project.techStack.length - 4}</span>
          )}
        </div>

        <div className="project-card-footer">
          <span className="learn-more">
            Learn More
            <svg className="arrow-icon" width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M8.293 10.293a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L9 8.586V1a1 1 0 00-2 0v7.586L4.707 6.293a1 1 0 00-1.414 1.414l3 3z" />
            </svg>
          </span>
        </div>
      </div>
    </Link>
  );
};

export default ProjectCard;
